import React, { useCallback, useMemo, useState } from "react";
import {
    Calendar,
    Culture,
    DateLocalizer,
    EventPropGetter,
    SlotInfo,
    View,
    Views,
    momentLocalizer,
} from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useLoaderData } from "react-router";

import { capitalize } from "lodash";
import moment from "moment";
import "moment-timezone";

import AddGameModal from "./components/Calendar/AddGameModal";
import ConfirmScheduleModal from "./components/Calendar/ConfirmScheduleModal";
import DeleteGameModal from "./components/Calendar/DeleteGameModal";
import Toolbar from "./components/Calendar/Toolbar";
import TournamentView from "./components/Calendar/TournamentView";

import { store } from "../..";
import { alertActions } from "../../features/alert/alertSlice";
import { globalActions } from "../../features/global/globalSlice";
import {
    selectSelectedTournament,
    tournamentManagementActions,
} from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
    getTemplate,
    getTournamentPlaceholders,
    getTournamentSchedule,
    movePlaceholder,
} from "../../lib/api";
import { isChildTournament } from "../../lib/utils";
import {
    IGame,
    IMasterTournamentSchedule,
    ITournamentTemplate,
} from "../../types/api";
import { ICalendarEvent, ICalendarResource } from "../../types/shared";

const Cal = withDragAndDrop(Calendar<ICalendarEvent, ICalendarResource>);

export const loader = async ({ params }: any) => {
    const tournamentId = parseInt(params.tournamentId);

    store.dispatch(
        tournamentManagementActions.setselectedTournamentId(
            parseInt(params.tournamentId),
        ),
    );

    const fields = store.getState().global.fields;

    let tournaments = store.getState().tournamentmanagement.tournaments;
    let masterTournaments =
        store.getState().tournamentmanagement.masterTournaments;

    const tournamentsPromise = tournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getTournaments());

    const masterTournamentsPromise = masterTournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getMasterTournaments());

    const fieldsPromise = fields.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getFields());

    const [{ games }, { template }, { schedule }] = await Promise.all([
        getTournamentPlaceholders(tournamentId),
        getTemplate("tournament", tournamentId),
        getTournamentSchedule(tournamentId),
        fieldsPromise,
        tournamentsPromise,
        masterTournamentsPromise,
    ]);

    const tournament = selectSelectedTournament(store.getState());
    tournaments = store.getState().tournamentmanagement.tournaments;
    masterTournaments = store.getState().tournamentmanagement.masterTournaments;

    const masterTournament = isChildTournament(tournament)
        ? masterTournaments.find(
              mt =>
                  mt.id ===
                  tournaments.find(t => t.id === tournamentId)?.parent,
          )
        : tournament;

    const header = isChildTournament(tournament)
        ? `${masterTournament?.name} - ${tournament.ageLevel} Umpire Schedule`
        : `${tournament?.name} - Full Tournament Umpire Schedule`;

    return {
        games,
        schedule,
        header,
        template,
    };
};

const localizer = momentLocalizer(moment);

const UmpireSchedule = (): React.JSX.Element => {
    const { games, template, schedule } = useLoaderData() as {
        games: IGame[];
        schedule: IMasterTournamentSchedule;
        template: ITournamentTemplate;
    };

    const tournament = useAppSelector(selectSelectedTournament);
    const { fields } = useAppSelector(state => state.global);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState<
        "confirm" | "unconfirm" | undefined
    >(undefined);
    const [eventToDelete, setEventToDelete] = useState<ICalendarEvent>();
    const [eventToAdd, setEventToAdd] = useState<SlotInfo>();
    const dispatch = useAppDispatch();

    const addGameEvent = (g: IGame, isBg?: boolean): ICalendarEvent => {
        let endDate: Date;
        const startDate = new Date(g.date);
        if (g.endDate) {
            endDate = new Date(g.endDate);
        } else {
            endDate = new Date(startDate);
            endDate.setHours(endDate.getHours() + 2);
            endDate.setMinutes(endDate.getMinutes() + 15);
        }

        const umpires: string[] = [];

        g.umpires?.map(u =>
            umpires.push(`${capitalize(u.location)}: ${u.name}`),
        );

        return {
            id: g.id,
            start: startDate,
            end: endDate,
            title: isBg ? "" : umpires.join("; "),
            resourceId: g.diamond || 0,
            ageLevel: g.ageLevel.ageLevel,
        };
    };

    const resources = useMemo(() => {
        const result: {
            resourceTitle: string;
            resourceId: number;
        }[] = [];

        for (const key in template.template.diamondRestrictions) {
            result.push({
                resourceTitle:
                    fields
                        .find(f => f.id === template.field)
                        ?.diamonds.find(d => d.id === parseInt(key))?.name ||
                    `Field ${key}`,
                resourceId: parseInt(key) || 0,
            });
        }
        return result;
    }, [fields, template]);

    const [events, setEvents] = useState<Array<ICalendarEvent>>(() => {
        const events: Array<ICalendarEvent> = [];

        games.map(g => {
            events.push(addGameEvent(g));
        });

        return events;
    });

    const [bgEvents, setBgEvents] = useState<Array<ICalendarEvent>>(() => {
        const events: Array<ICalendarEvent> = [];

        schedule.games?.forEach(group => {
            group.games?.forEach(g => {
                events.push(addGameEvent(g, true));
            });
        });

        return events;
    });

    const convertDate = useCallback((dateString: string, hours: number) => {
        const date = new Date(dateString);
        date.setHours(hours, 0, 0, 0);
        return date;
    }, []);

    const handleMove = useCallback(
        async ({
            event,
            start,
            end,
            resourceId,
        }: {
            event: ICalendarEvent;
            start: Date | string;
            end: Date | string;
            resourceId?: string | number;
        }) => {
            setEvents(prev => {
                const existing =
                    prev.find(
                        ev =>
                            ev.ageLevel === event.ageLevel &&
                            ev.start === event.start &&
                            ev.end === event.end &&
                            ev.resourceId === event.resourceId,
                    ) ?? ({} as ICalendarEvent);

                const filtered = prev.filter(ev => ev != existing);

                return [...filtered, { ...existing, start, end, resourceId }];
            });

            movePlaceholder({
                prev: {
                    tournamentId: tournament?.id as number,
                    field: tournament?.field?.id as number,
                    diamond: event.resourceId as number,
                    date: event.start.toLocaleString(),
                    endDate: event.end.toLocaleString(),
                    ageLevel: event.ageLevel,
                },
                newInfo: {
                    date: start.toLocaleString(),
                    endDate: end.toLocaleString(),
                    diamond: resourceId as number,
                },
            })
                .then(response => {
                    dispatch(
                        alertActions.success({
                            title: `Placeholder game rescheduled successfully`,
                            dismissible: true,
                            scroll: false,
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem rescheduling the game.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                });
        },
        [setEvents],
    );

    const { defaultDate, defaultView, views, formats, minDate, maxDate } =
        useMemo(
            () => ({
                defaultDate: tournament?.startDate
                    ? new Date(tournament.startDate)
                    : new Date(),
                defaultView: Views.DAY,
                views: { day: true, week: TournamentView },
                formats: {
                    dayFormat: (
                        date: Date,
                        culture?: Culture,
                        localizer?: DateLocalizer,
                    ) => localizer?.format(date, "ddd M/DD", culture) || "",
                },
                minDate: tournament?.startDate
                    ? convertDate(tournament.startDate, 7)
                    : new Date(),
                maxDate: tournament?.endDate
                    ? convertDate(tournament.startDate, 23)
                    : new Date(),
            }),
            [],
        );

    const [view, setView] = useState<View>(defaultView);
    const onView = useCallback((newView: View) => setView(newView), [setView]);

    const eventPropGetter: EventPropGetter<ICalendarEvent> = useCallback(
        event => ({
            className: `tournamentAge${event.ageLevel}${bgEvents.length > 0 ? ` hasBgEvents` : ``}`,
        }),
        [],
    );

    return (
        <React.Fragment>
            <Cal
                backgroundEvents={bgEvents}
                components={{
                    toolbar: props => (
                        <Toolbar
                            isUmpireSchedule={true}
                            {...props}
                            hasBgEvents={bgEvents.length > 0}
                            setShowConfirmModal={setShowConfirmModal}
                        />
                    ),
                }}
                defaultDate={defaultDate}
                defaultView={defaultView}
                events={events}
                eventPropGetter={eventPropGetter}
                formats={formats}
                localizer={localizer}
                max={maxDate}
                messages={{
                    week: "Tournament Calendar",
                    agenda: "Tournament Schedule",
                }}
                min={minDate}
                onEventDrop={handleMove}
                onEventResize={handleMove}
                onView={onView}
                onDoubleClickEvent={props => {
                    setEventToDelete(props);
                    setShowDeleteModal(true);
                }}
                onSelectSlot={props => {
                    const diff = Math.abs(
                        props.start.getTime() - props.end.getTime(),
                    );
                    if (diff >= 60 * 60 * 1000) {
                        setEventToAdd(props);
                        setShowAddModal(true);
                    }
                }}
                resizable
                resources={resources}
                resourceIdAccessor="resourceId"
                resourceTitleAccessor="resourceTitle"
                selectable
                step={15}
                timeslots={4}
                view={view}
                views={views}
            />
            {showDeleteModal && eventToDelete && (
                <DeleteGameModal
                    {...eventToDelete}
                    resources={resources}
                    show={showDeleteModal}
                    setShow={setShowDeleteModal}
                    setEvents={setEvents}
                />
            )}
            {showAddModal && eventToAdd && (
                <AddGameModal
                    {...eventToAdd}
                    resources={resources}
                    show={showAddModal}
                    setShow={setShowAddModal}
                    setEvents={setEvents}
                />
            )}
            {showConfirmModal && (
                <ConfirmScheduleModal
                    show={showConfirmModal}
                    setShow={setShowConfirmModal}
                />
            )}
        </React.Fragment>
    );
};

export default UmpireSchedule;
